/* eslint-disable default-case */
import React, { useState, useEffect } from "react";
import Input from "../../components/Input";
import { Form, Upload, message, Button, Row, Col, Typography } from "antd";
import { UploadOutlined, LoadingOutlined } from "@ant-design/icons";
import getBase64 from "../../utils/getBase64";
import classNames from "classnames";
import imageCompression from "browser-image-compression";
import Compressor from "compressorjs";

import { ExclamationCircleOutlined } from "@ant-design/icons";

const identificationDescription = {
  MEI: "RG/CPF do Titular da Conta",
  ME: "RG/CPF do Diretor",
  LIMITED: "RG/CPF do Diretor",
  INDIVIDUAL: "RG/CPF do sócio administrador",
  ASSOCIATION: "RG/CPF do Presidente",
};

const companyFields = {
  MEI: [{ name: "IDENTIFICATION", label: identificationDescription["MEI"] }],
  ME: [
    { name: "IDENTIFICATION", label: identificationDescription["LIMITED"] },
    {
      name: "SOCIAL_CONTRACT",
      label: "Contrato Social",
    },
  ],
  LIMITED: [
    { name: "IDENTIFICATION", label: identificationDescription["LIMITED"] },
    {
      name: "SOCIAL_CONTRACT",
      label: "Contrato Social",
    },
  ],
  INDIVIDUAL: [
    { name: "IDENTIFICATION", label: identificationDescription["INDIVIDUAL"] },
    {
      name: "ENTREPRENEUR_REQUIREMENT",
      label: "Requerimento de Empresário/Junta Comercial",
    },
  ],
  ASSOCIATION: [
    { name: "IDENTIFICATION", label: identificationDescription["ASSOCIATION"] },
    {
      name: "MINUTES_OF_ELECTION",
      label: "Ata de eleição da última diretoria",
    },
  ],
};

export default function FormDocuments({
  register,
  errors,
  setValue,
  getValues,
  onPrevious,
  onNext,
  loading,
}) {
  const [formFiles] = Form.useForm();

  const { Text } = Typography;

  const [documentSelected, setDocumentSelected] = useState(0);
  const [fields, setFields] = useState([]);

  const [docFileList, setDocFileList] = useState({
    SOCIAL_CONTRACT: [],
    IDENTIFICATION: [],
    ENTREPRENEUR_REQUIREMENT: [],
    MINUTES_OF_ELECTION: [],
  });

  const handleRemoveUpload = async (name) => {
    setDocFileList({ ...docFileList, [name]: [] });
    setValue(name, null, { shouldValidate: true });
  };

  const formatDocument = ({ base64, name, formatType, fileName }) => {
    let description, type, format;
    const { companyType } = getValues();

    switch (name) {
      case "SOCIAL_CONTRACT":
        description = "Contrato Social";
        type = 5;
        break;

      case "IDENTIFICATION":
        description = identificationDescription[companyType];
        type = 0;
        break;
      case "ENTREPRENEUR_REQUIREMENT":
        description = "Requerimento de Empresário/Junta Comercial";
        type = 7;
        break;
      case "MINUTES_OF_ELECTION":
        description = "Ata de eleição da última diretoria";
        type = 3;
        break;
    }

    switch (formatType) {
      case "application/pdf":
        format = 0;
        break;
      case "image/jpg":
        format = 1;
        break;
      case "image/jpeg":
        format = 2;
        break;
      case "text/plain":
        format = 3;
        break;
      case "image/png":
        format = 4;
        break;
    }

    const doc = {
      documentFile: base64,
      documentName: name,
      description: description,
      documentType: type,
      documentFormat: format,
      documentFileName: fileName,
    };

    setValue(name, doc, { shouldValidate: true });
  };

  const handleChangeUpload = async ({ fileList, name }) => {
    const fileImage = fileList[0];
    if (fileImage) {
      // check if is image or pdf
      try {
        let base64;
        let formatType;
        let fileName;

        if (["image/jpeg", "image/jpg", "image/png"].includes(fileImage.type)) {
          const options = {
            quality: 0.6,
            mimeType: "image/jpeg",
            maxWidth: 1000,
            maxHeight: 1000,
          };

          const compressedFile = new Compressor(fileImage.originFileObj, {
            ...options,
            async success(result) {
              setDocFileList({ ...docFileList, [name]: [result] });

              base64 = await getBase64(result);
              formatType = result.type;
              fileName = result.name;

              formatDocument({ base64, formatType, fileName, name });
            },
          });
        } else {
          setDocFileList({ ...docFileList, [name]: fileList });

          base64 = await getBase64(fileImage.originFileObj);
          formatType = fileImage.type;
          fileName = fileImage.name;

          formatDocument({ base64, formatType, fileName, name });
        }
      } catch (err) {
        message.error(err?.response?.data?.message);
        // Show the user a toast message or notification that something went wrong while compressing file
      }
    }
  };

  useEffect(() => {
    const {
      companyType,
      SOCIAL_CONTRACT,
      IDENTIFICATION,
      ENTREPRENEUR_REQUIREMENT,
      MINUTES_OF_ELECTION,
    } = getValues();

    const docList = {
      SOCIAL_CONTRACT: SOCIAL_CONTRACT
        ? [
            {
              uid: "1",
              name: SOCIAL_CONTRACT.documentFileName,
              url: SOCIAL_CONTRACT.documentFile,
            },
          ]
        : [],
      IDENTIFICATION: IDENTIFICATION
        ? [
            {
              uid: "1",
              name: IDENTIFICATION.documentFileName,
              url: IDENTIFICATION.documentFile,
            },
          ]
        : [],
      ENTREPRENEUR_REQUIREMENT: ENTREPRENEUR_REQUIREMENT
        ? [
            {
              uid: "1",
              name: ENTREPRENEUR_REQUIREMENT.documentFileName,
              url: ENTREPRENEUR_REQUIREMENT.documentFile,
            },
          ]
        : [],
      MINUTES_OF_ELECTION: MINUTES_OF_ELECTION
        ? [
            {
              uid: "1",
              name: MINUTES_OF_ELECTION.documentFileName,
              url: MINUTES_OF_ELECTION.documentFile,
            },
          ]
        : [],
    };

    setDocFileList(docList);

    SOCIAL_CONTRACT && formFiles.setFieldsValue({ SOCIAL_CONTRACT: true });
    IDENTIFICATION && formFiles.setFieldsValue({ IDENTIFICATION: true });
    ENTREPRENEUR_REQUIREMENT &&
      formFiles.setFieldsValue({ ENTREPRENEUR_REQUIREMENT: true });
    MINUTES_OF_ELECTION &&
      formFiles.setFieldsValue({ MINUTES_OF_ELECTION: true });

    setFields(companyFields[companyType]);
  }, []);

  return (
    <div key="formDocuments">
      <Form
        form={formFiles}
        name="documents"
        onFinish={onNext}
        layout="horizontal"
      >
        <Row gutter={16} className="__auth-mb-25">
          {fields?.length > 0 &&
            fields.map((item, index) => (
              <Col xs={24} style={{ marginBottom: "50px 0" }} key={index}>
                <Form.Item
                  name={item.name}
                  label={item.label}
                  valuePropName="files"
                  getValueFromEvent={(e) => {
                    if (Array.isArray(e)) {
                      return e;
                    }
                    return e && e.fileList;
                  }}
                  rules={
                    [
                      // {
                      //   required: ["SOCIAL_CONTRACT"].includes(item.name),
                      //   message: "Arquivo é obrigatório",
                      // },
                    ]
                  }
                >
                  <Upload
                    onPreview={() => null}
                    beforeUpload={(file) => {
                      let valid = true;

                      if (
                        ![
                          "image/png",
                          "image/jpg",
                          "image/jpeg",
                          "application/pdf",
                        ].includes(file.type)
                      ) {
                        message.error(
                          `${file.name} não possui um formato válido`
                        );
                        valid = false;
                      }

                      if (file.size > 5000000) {
                        message.error(
                          "O arquivo possui tamanho superior a 5mb"
                        );
                        valid = false;
                      }

                      return valid ? false : Upload.LIST_IGNORE;
                    }}
                    listType="picture"
                    maxCount={1}
                    accept=".png, .jpg, .jpeg, application/pdf"
                    onChange={({ fileList }) =>
                      handleChangeUpload({ fileList, name: item.name })
                    }
                    onRemove={() => {
                      handleRemoveUpload(item.name);
                    }}
                    fileList={docFileList[item.name]}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      className="__product-width-100 __product-overflow-hidden"
                    >
                      <Text>Clique para enviar</Text>
                    </Button>
                  </Upload>
                </Form.Item>
                <>
                  <p>
                    {errors[item.name]?.documentFile?.message ||
                      errors[item.name]?.message}
                  </p>
                </>
              </Col>
            ))}
        </Row>
      </Form>

      <Row gutter={16} className="__signup-bottom-button">
        <Col xs={12}>
          <button
            type="button"
            className="__auth-button-outlined"
            onClick={onPrevious}
          >
            Voltar
          </button>
        </Col>
        <Col xs={12}>
          <button
            form="documents"
            type="button"
            className="__auth-button-primary"
            onClick={onNext}
          >
            {loading ? <LoadingOutlined /> : "Próximo"}
          </button>
        </Col>
      </Row>
    </div>
  );
}
